/**
 * Genera planillas (score sheets) imprimibles en PDF para que los jueces las
 * llenen a mano durante el evento y luego el administrador pase los resultados
 * al sistema.
 *
 * Una planilla por actividad: cabecera (evento/categoría/actividad + rúbrica),
 * tabla con todos los clubes inscritos y columnas según el modo de puntaje
 * (podio / cumple-no / entregó / bandas de tiempo / numérico), y pie con
 * espacio para nombre del juez, firma y fecha. Pagina automáticamente.
 */
import { jsPDF } from "jspdf";
import type {
  ActivityScoreConfig,
  ActivityScoreMode,
  EventActivity,
} from "@/types";

interface SheetClub {
  id: string;
  name: string;
}

interface SheetParams {
  eventName: string;
  categoryName: string;
  activity: Pick<
    EventActivity,
    "name" | "scoreMode" | "scoreConfig" | "longDescription"
  >;
  clubs: SheetClub[];
}

const PAGE_MARGIN = 14; // mm
const ROW_H = 8; // mm por fila de club

interface Column {
  header: string;
  sub?: string;
  width: number; // mm
  /** Celda de marca (cuadro) vs celda de escritura libre. */
  check?: boolean;
}

const MODE_LABEL: Record<ActivityScoreMode, string> = {
  podium: "Podio (1°/2°/3°/Participación)",
  pass_fail: "Cumple / No cumple",
  fixed: "Entregó / No entregó",
  timing: "Por fecha de entrega",
  deductive: "Deductivo (base − faltas)",
  accumulative: "Acumulativo (base + unidades − penalizaciones)",
};

/** Columnas de puntaje según el modo, con sus anchos. */
function scoreColumns(
  mode: ActivityScoreMode,
  cfg: ActivityScoreConfig,
): Column[] {
  const n = (v: number | undefined) => (v ?? 0).toLocaleString("es-CO");
  switch (mode) {
    case "podium": {
      const r = cfg.podium ?? {
        first: 0,
        second: 0,
        third: 0,
        participation: 0,
      };
      return [
        { header: "1°", sub: n(r.first), width: 20, check: true },
        { header: "2°", sub: n(r.second), width: 20, check: true },
        { header: "3°", sub: n(r.third), width: 20, check: true },
        { header: "Part.", sub: n(r.participation), width: 20, check: true },
      ];
    }
    case "pass_fail":
      return [
        { header: "Cumple", sub: n(cfg.passFailValue), width: 26, check: true },
        { header: "No cumple", sub: "0", width: 26, check: true },
      ];
    case "fixed":
      return [
        { header: "Entregó", sub: n(cfg.fixedValue), width: 26, check: true },
        { header: "No entregó", sub: "0", width: 26, check: true },
      ];
    case "timing": {
      const bands = cfg.timingBands ?? [];
      if (bands.length === 0) {
        return [{ header: "Puntos", width: 40 }];
      }
      const w = Math.max(18, Math.min(34, 100 / bands.length));
      return bands.map((b) => ({
        header: b.label || "Banda",
        sub: n(b.points),
        width: w,
        check: true,
      }));
    }
    case "deductive":
      return [
        { header: "Faltas", sub: "(− pts)", width: 30 },
        { header: "Total", sub: `base ${n(cfg.deductiveBase)}`, width: 30 },
      ];
    case "accumulative":
      return [
        {
          header: "Unidades",
          sub: `× ${n(cfg.accumulativePerUnit)}`,
          width: 28,
        },
        {
          header: "Penaliz.",
          sub: `× ${n(cfg.accumulativePenaltyPerUnit)}`,
          width: 28,
        },
        { header: "Total", sub: `base ${n(cfg.accumulativeBase)}`, width: 28 },
      ];
    default:
      return [{ header: "Puntos", width: 40 }];
  }
}

/** Texto de la rúbrica (resumen de puntos) para la cabecera. */
function rubricSummary(
  mode: ActivityScoreMode,
  cfg: ActivityScoreConfig,
): string {
  const n = (v: number | undefined) => (v ?? 0).toLocaleString("es-CO");
  switch (mode) {
    case "podium": {
      const r = cfg.podium ?? {
        first: 0,
        second: 0,
        third: 0,
        participation: 0,
      };
      return `1° = ${n(r.first)} · 2° = ${n(r.second)} · 3° = ${n(r.third)} · Participación = ${n(r.participation)}`;
    }
    case "pass_fail":
      return `Cumple = ${n(cfg.passFailValue)} · No cumple = 0`;
    case "fixed":
      return `Entregó = ${n(cfg.fixedValue)} · No entregó = 0`;
    case "timing":
      return (cfg.timingBands ?? [])
        .map((b) => `${b.label} = ${n(b.points)}`)
        .join(" · ");
    case "deductive":
      return `Puntaje base = ${n(cfg.deductiveBase)} (se restan las faltas)`;
    case "accumulative":
      return `Base = ${n(cfg.accumulativeBase)} · por unidad = ${n(cfg.accumulativePerUnit)} · penalización = ${n(cfg.accumulativePenaltyPerUnit)}`;
    default:
      return "";
  }
}

/**
 * Dibuja la planilla de UNA actividad en el PDF dado, comenzando en una página
 * nueva. Devuelve el pdf para encadenar.
 */
function drawActivitySheet(pdf: jsPDF, params: SheetParams, isFirst: boolean) {
  if (!isFirst) pdf.addPage();
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - PAGE_MARGIN * 2;

  const cols = scoreColumns(params.activity.scoreMode, params.activity.scoreConfig);
  const obsW = 30;
  const idxW = 8;
  const scoreW = cols.reduce((s, c) => s + c.width, 0);
  const clubW = Math.max(40, usableW - idxW - scoreW - obsW);

  const drawHeader = () => {
    let y = PAGE_MARGIN;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text("PLANILLA DE PUNTAJE", PAGE_MARGIN, y + 2);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(params.eventName, pageW - PAGE_MARGIN, y + 2, { align: "right" });
    y += 9;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text(params.activity.name, PAGE_MARGIN, y);
    y += 5;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(90);
    pdf.text(
      `Categoría: ${params.categoryName}  ·  ${MODE_LABEL[params.activity.scoreMode]}`,
      PAGE_MARGIN,
      y,
    );
    y += 4.5;
    const rubric = rubricSummary(
      params.activity.scoreMode,
      params.activity.scoreConfig,
    );
    if (rubric) {
      const lines = pdf.splitTextToSize(`Puntos: ${rubric}`, usableW);
      pdf.text(lines, PAGE_MARGIN, y);
      y += lines.length * 4;
    }
    pdf.setTextColor(0);
    return y + 2;
  };

  const drawTableHead = (y: number) => {
    pdf.setFillColor(238, 238, 245);
    pdf.rect(PAGE_MARGIN, y, usableW, 9, "F");
    pdf.setDrawColor(180);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    let x = PAGE_MARGIN;
    const cell = (w: number, h: string, sub?: string) => {
      pdf.text(h, x + 1.5, y + 4);
      if (sub) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(110);
        pdf.text(sub, x + 1.5, y + 7.5);
        pdf.setTextColor(0);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
      }
      x += w;
    };
    cell(idxW, "#");
    cell(clubW, "Club");
    cols.forEach((c) => cell(c.width, c.header, c.sub));
    cell(obsW, "Observaciones");
    // bordes verticales del head
    pdf.rect(PAGE_MARGIN, y, usableW, 9);
    return y + 9;
  };

  let y = drawHeader();
  y = drawTableHead(y);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  const bottomLimit = pageH - 28; // dejar espacio para el pie de firma

  params.clubs.forEach((club, i) => {
    if (y + ROW_H > bottomLimit) {
      pdf.addPage();
      y = drawHeader();
      y = drawTableHead(y);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
    }
    // fila
    let x = PAGE_MARGIN;
    pdf.setDrawColor(210);
    pdf.rect(PAGE_MARGIN, y, usableW, ROW_H);
    // # y club
    pdf.text(String(i + 1), x + 1.5, y + 5.3);
    x += idxW;
    pdf.line(x, y, x, y + ROW_H);
    const clubName = pdf.splitTextToSize(club.name, clubW - 3)[0] as string;
    pdf.text(clubName, x + 1.5, y + 5.3);
    x += clubW;
    pdf.line(x, y, x, y + ROW_H);
    // columnas de puntaje
    cols.forEach((c) => {
      if (c.check) {
        // cuadro de marca centrado
        const boxSize = 4;
        const bx = x + c.width / 2 - boxSize / 2;
        const by = y + ROW_H / 2 - boxSize / 2;
        pdf.setDrawColor(120);
        pdf.rect(bx, by, boxSize, boxSize);
        pdf.setDrawColor(210);
      }
      x += c.width;
      pdf.line(x, y, x, y + ROW_H);
    });
    // observaciones (queda en blanco)
    y += ROW_H;
  });

  // Pie: juez / firma / fecha
  const footY = pageH - 20;
  pdf.setDrawColor(120);
  pdf.setFontSize(9);
  pdf.line(PAGE_MARGIN, footY, PAGE_MARGIN + 60, footY);
  pdf.text("Juez (nombre)", PAGE_MARGIN, footY + 4);
  pdf.line(PAGE_MARGIN + 70, footY, PAGE_MARGIN + 120, footY);
  pdf.text("Firma", PAGE_MARGIN + 70, footY + 4);
  pdf.line(PAGE_MARGIN + 130, footY, PAGE_MARGIN + 175, footY);
  pdf.text("Fecha", PAGE_MARGIN + 130, footY + 4);
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Construye (sin descargar) el PDF de la planilla de UNA actividad. */
export function buildActivityScoreSheetPdf(params: SheetParams): jsPDF {
  const pdf = new jsPDF({ unit: "mm", format: "letter" });
  drawActivitySheet(pdf, params, true);
  return pdf;
}

/** Descarga la planilla PDF de UNA actividad. */
export function downloadActivityScoreSheet(params: SheetParams): void {
  buildActivityScoreSheetPdf(params).save(
    `planilla-${slug(params.categoryName)}-${slug(params.activity.name)}.pdf`,
  );
}

/** Descarga UN PDF con las planillas de TODAS las actividades dadas. */
export function downloadAllActivityScoreSheets(params: {
  eventName: string;
  sheets: Array<{
    categoryName: string;
    activity: SheetParams["activity"];
  }>;
  clubs: SheetClub[];
}): void {
  if (params.sheets.length === 0) return;
  const pdf = new jsPDF({ unit: "mm", format: "letter" });
  params.sheets.forEach((s, i) => {
    drawActivitySheet(
      pdf,
      {
        eventName: params.eventName,
        categoryName: s.categoryName,
        activity: s.activity,
        clubs: params.clubs,
      },
      i === 0,
    );
  });
  pdf.save(`planillas-${slug(params.eventName)}.pdf`);
}
